import { Component, Input, Output, EventEmitter, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzUploadModule, NzUploadFile } from 'ng-zorro-antd/upload';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';

export interface AiPromptSuggestion {
  title: string;
  description: string;
}

/**
 * PURE FUNCTION: Maps a file extension to a semantic FontAwesome icon class.
 * @param {string} fileName - The name of the file to evaluate.
 * @param {string} [mimeType] - Optional mimeType for advanced mapping.
 * @returns {string} The CSS classes for the FontAwesome icon.
 */
export function getAiPromptFileIcon(fileName: string, mimeType?: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['json'].includes(ext)) return 'fas fa-file-code text-warning';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fas fa-file-excel text-success';
  if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word text-info';
  if (['pdf'].includes(ext)) return 'fas fa-file-pdf text-danger';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'fas fa-file-image text-primary';
  return 'fas fa-file-alt text-secondary-text';
}


/**
 * @class AiPromptComponent
 * @description A reusable dumb component for rendering an AI prompt input interface.
 * Contains a textarea, optional suggestions carousel, file upload, and submit/cancel buttons.
 */
@Component({
  selector: 'rapider-ai-prompt',
  standalone: true,
  imports: [CommonModule, FormsModule, NzInputModule, NzButtonModule, NzUploadModule, NzCarouselModule],
  templateUrl: './ai-prompt.component.html'
})
export class AiPromptComponent {
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() placeholder: string = 'Type your request here...';
  @Input() submitText: string = 'Submit';
  @Input() cancelText: string = 'Cancel';
  @Input() uploadText: string = 'Upload Reference Files';
  @Input() isSubmitting: boolean = false;

  /** Allows the user to click the submit button even if the prompt text area is completely empty. */
  @Input() allowEmptySubmit: boolean = false;

  @Input() suggestions: AiPromptSuggestion[] = [];

  @Output() submitPrompt = new EventEmitter<{ promptText: string, files: File[] }>();
  @Output() cancel = new EventEmitter<void>();

  public promptText = signal<string>('');
  public selectedFiles = signal<File[]>([]);

  public suggestionChunks = computed(() => this.chunkArray(this.suggestions, 4));

  public getFileIcon = getAiPromptFileIcon;

  /**
   * Removes a file from the selected files list.
   * @param {File} fileToRemove - The file instance to filter out.
   */
  public removeFile(fileToRemove: File): void {
    this.selectedFiles.update(files => files.filter(f => f !== fileToRemove));
  }

  /**
   * Appends a clicked suggestion to the textarea.
   */
  public selectSuggestion(suggestion: AiPromptSuggestion): void {
    const current = this.promptText();
    this.promptText.set(current ? `${current}\n${suggestion.description}` : suggestion.description);
  }

  /**
   * Intercepts the upload to prevent auto-uploading to a server, storing the file locally instead.
   */
  public beforeUpload = (file: NzUploadFile): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const physicalFile = (file as any).originFileObj || (file as any as File);
    this.selectedFiles.update(files => [...files, physicalFile]);
    return false;
  };

  public onSubmit(): void {
    // Prevent submission if text is empty AND empty submits are not explicitly allowed
    if (!this.allowEmptySubmit && !this.promptText().trim()) return;

    this.submitPrompt.emit({
      promptText: this.promptText(),
      files: this.selectedFiles()
    });
  }

  public onCancel(): void {
    this.promptText.set('');
    this.selectedFiles.set([]);
    this.cancel.emit();
  }

  /**
   * Pure function to chunk the suggestions array for carousel pages.
   */
  private chunkArray<T>(array: T[], size: number): T[][] {
    if (!array || size <= 0) return [];
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }
}